import {ScreenLayout} from "@/components/layouts/page/ScreenLayout";
import TableLayout from "@/components/layouts/page/TableLayout";
import jnjLibrary from "@/pages/common/LibraryPage/jnjLibrary.json";
import {
  getVideos,
  setPage,
  setSearch,
  setSelectedData,
  setSize,
  setSort,
  setTotalPages,
} from "@/redux/slices/videoSlices/tableSlice";

import {useCallback, useEffect, useMemo} from "react";

import {ControlButton} from "@ui/buttons/ControlButton";
import {TableButton} from "@ui/buttons/TableButton";
import {RootCheckbox} from "@ui/inputs/RootCheckbox";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

const JNJ_REMOTE_ORIGIN =
  import.meta.env.VITE_JNJ_REMOTE_ORIGIN || "https://jnj.cosmart.app";

const resolveJnjDocumentUrl = (url = "", name = "") => {
  if (!url && !name) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/")) return `${JNJ_REMOTE_ORIGIN}${url}`;
  if (url) return `${JNJ_REMOTE_ORIGIN}/video/jnj/${encodeURIComponent(url)}`;
  return `${JNJ_REMOTE_ORIGIN}/video/jnj/${encodeURIComponent(name)}`;
};

const compareValues = (a, b, direction = "asc") => {
  if (a === b) return 0;

  if (a == null) return direction === "asc" ? -1 : 1;
  if (b == null) return direction === "asc" ? 1 : -1;

  const result =
    typeof a === "number" && typeof b === "number"
      ? a - b
      : String(a).localeCompare(String(b), undefined, {
          numeric: true,
          sensitivity: "base",
        });

  return direction === "desc" ? -result : result;
};

export const VideoTablePage = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {demo} = useSelector((state) => state.common.candidate);
  const {page, size, sort, totalPages, status, search} = useSelector(
    (state) => state.video.table,
  );
  const {isJnj, isAbbott, isAkvion} = useSelector(
    (state) => state.common.settings,
  );

  const servier_test_server = window.location.host.includes("servier");
  const sanofi_test_server = window.location.host.includes("sanofi");
  const usesStaticLibraryData =
    servier_test_server || sanofi_test_server || isJnj;

  const handleClickDeactivateAll = () => {
    console.log("Deactivated check videos!");
  };

  const handleCheckFilter = () => {
    console.log("Show inactive videos!");
  };

  const handleSearch = useCallback(
    (value) => {
      clearTimeout(window.searchTimeout);

      window.searchTimeout = setTimeout(() => {
        dispatch(setSearch(value));
        dispatch(setPage(1));

        if (!isJnj) {
          dispatch(getVideos({page: 1, size, search: value}));
        }
      }, 500);
    },
    [dispatch, isJnj, size],
  );

  const goToPage = (pageIndex) => {
    dispatch(setPage(pageIndex + 1));
  };

  const handleSelectSize = (option) => {
    dispatch(setSize(+option));
    dispatch(setPage(1));

    if (!isJnj) {
      dispatch(setTotalPages(Math.ceil(totalPages / size)));
    }
  };

  const handleSelectSort = (option) => {
    dispatch(setSort(option));
  };

  const controls = [
    {
      id: 0,
      button: (
        <ControlButton
          color={"var(--green)"}
          txt={t("button.add")}
          link
        />
      ),
    },
    {
      id: 1,
      button: (
        <ControlButton
          color={"var(--red)"}
          txt={t("button.deactivate")}
          onClick={() => handleClickDeactivateAll()}
        />
      ),
    },
  ];

  const jnjRows = useMemo(() => {
    if (!isJnj) return [];

    return jnjLibrary.map((item, id) => ({
      id,
      name: item.name,
      product: item.category || "—",
      created_at: item.uploaded_at,
      url: resolveJnjDocumentUrl(item.url || "", item.name || ""),
      inactive: false,
    }));
  }, [isJnj]);

  const filteredJnjRows = useMemo(() => {
    if (!isJnj) return [];

    const query = (search || "").trim().toLowerCase();
    if (!query) return jnjRows;

    return jnjRows.filter((row) =>
      [row.name, row.product, row.created_at].some((value) =>
        String(value).toLowerCase().includes(query),
      ),
    );
  }, [isJnj, jnjRows, search]);

  const sortedJnjRows = useMemo(() => {
    if (!isJnj) return [];

    const field = sort?.value;
    const direction = sort?.direction || "asc";
    if (!field) return filteredJnjRows;

    return [...filteredJnjRows].sort((left, right) => {
      if (field === "created_at") {
        return compareValues(
          new Date(left.created_at).getTime(),
          new Date(right.created_at).getTime(),
          direction,
        );
      }

      return compareValues(left[field], right[field], direction);
    });
  }, [filteredJnjRows, isJnj, sort]);

  const jnjTotalPages = useMemo(() => {
    if (!isJnj) return 1;

    return Math.max(1, Math.ceil(sortedJnjRows.length / size));
  }, [isJnj, size, sortedJnjRows.length]);

  const paginatedJnjRows = useMemo(() => {
    if (!isJnj) return [];

    const start = (page - 1) * size;

    return sortedJnjRows.slice(start, start + size);
  }, [isJnj, page, size, sortedJnjRows]);

  const data = useMemo(() => {
    if (servier_test_server) {
      return [
        {
          id: 0,
          name: t("video_servier_1.name"),
          product: t("video_servier_1.product"),
          line: t("video_servier_1.line"),
          created_at: "2025-05-28",
          inactive: false,
        },
        {
          id: 1,
          name: t("video_servier_2.name"),
          product: t("video_servier_2.product"),
          line: t("video_servier_2.line"),
          created_at: "2025-05-28",
          inactive: false,
        },
      ];
    }

    if (sanofi_test_server) {
      return [
        {
          id: 0,
          name: t("video_sanofi_1.name"),
          product: t("video_sanofi_1.product"),
          line: t("video_sanofi_1.line"),
          created_at: "2025-05-28",
          inactive: false,
        },
      ];
    }

    return [
      {
        id: 0,
        name: t("video_1.name"),
        product: t("video_1.product"),
        line: t("video_1.line"),
        created_at: "2024-10-21",
        inactive: false,
      },
      {
        id: 1,
        name: t("video_2.name"),
        product: t("video_2.product"),
        line: t("video_2.line"),
        created_at: "2024-10-21",
        inactive: false,
      },
      {
        id: 2,
        name: t("video_3.name"),
        product: t("video_3.product"),
        line: t("video_3.line"),
        created_at: "2024-10-21",
        inactive: false,
      },
      {
        id: 3,
        name: t("video_4.name"),
        product: t("video_4.product"),
        line: t("video_4.line"),
        created_at: "2024-10-21",
        inactive: false,
      },
    ];
  }, [servier_test_server, sanofi_test_server, t]);

  const tableData = isJnj ? paginatedJnjRows : isAbbott || isAkvion ? [] : data;

  const tableTotalPages = isJnj ? jnjTotalPages : totalPages;

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: t("table.name"),
        accessor: "name",
        Cell: ({cell: {value}}) => (
          <span dangerouslySetInnerHTML={{__html: value}} />
        ),
        width: isJnj ? 680 : 400,
      },
      {
        Header: t("table.product"),
        accessor: "product",
        Cell: ({cell: {value}}) => (
          <span dangerouslySetInnerHTML={{__html: value}} />
        ),
        width: isJnj ? 385 : 260,
      },
      ...(!isJnj
        ? [
            {
              Header: t("table.product_line"),
              accessor: "line",
              Cell: ({cell: {value}}) => (
                <span dangerouslySetInnerHTML={{__html: value}} />
              ),
              width: 320,
            },
          ]
        : []),
      {
        Header: t("table.created"),
        accessor: "created_at",
        Cell: ({cell: {value}}) =>
          value ? moment(value).format("DD-MM-YYYY") : "",
        width: isJnj ? 325 : 225,
      },
      {
        id: "actions",
        Header: t("table.actions"),
        Cell: ({row}) => (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginLeft: "auto",
              width: "fit-content",
            }}
            className={"flex gap-[8rem] phone:gap-[40rem]"}
          >
            {isJnj && row.original.url ? (
              <TableButton
                icon={"view"}
                color={"var(--orange)"}
                label={t("table.button.view")}
                action={"item"}
                item={row.original}
                onClickAction={() =>
                  window.open(row.original.url, "_blank", "noopener,noreferrer")
                }
              />
            ) : (
              <TableButton
                icon={"view"}
                color={"var(--orange)"}
                label={t("table.button.view")}
                action={"view"}
                item={row.original}
              />
            )}

            {!demo && !isJnj && (
              <>
                <TableButton
                  icon={"edit"}
                  color={"var(--blueLight)"}
                  label={t("table.button.edit")}
                  action={"edit"}
                  item={row.original}
                />

                <TableButton
                  icon={"deactivate"}
                  color={"var(--red)"}
                  label={t("table.button.deactivate")}
                  action={"deactivate"}
                  item={row.original}
                />
              </>
            )}
          </div>
        ),
        width: isJnj ? 72 : 210,
      },
    ];

    if (!demo && !isJnj) {
      baseColumns.unshift({
        id: "selection",
        Header: ({getToggleAllRowsSelectedProps}) => (
          <div>
            <RootCheckbox {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({row}) => (
          <div>
            <RootCheckbox {...row.getToggleRowSelectedProps()} />
          </div>
        ),
        width: 50,
      });
    }

    return baseColumns;
  }, [demo, isJnj, t]);

  useEffect(() => {
    if (usesStaticLibraryData) return;

    if (Object.keys(sort).length !== 0) {
      dispatch(getVideos({page, size}));
    }
  }, [dispatch, page, size, sort, usesStaticLibraryData]);

  useEffect(() => {
    if (!isJnj || page <= jnjTotalPages) return;

    dispatch(setPage(jnjTotalPages));
  }, [dispatch, isJnj, jnjTotalPages, page]);

  useEffect(() => {
    const firstAccessor = columns.find((column) => column.accessor);

    dispatch(
      setSort({name: firstAccessor.Header, value: firstAccessor.accessor}),
    );
  }, []);

  return (
    // тестовая data
    // <ScreenLayout controlsList={!demo && controls} title={'Training Library'}>
    // 	<TableLayout
    // 		columns={columns}
    // 		data={data}
    // 		filterCheckbox={
    // 			!demo && (
    // 				<RootCheckbox
    // 					onChange={() => alert('filter-table')}
    // 					label={'Show Inactive Videos'}
    // 				/>
    // 			)
    // 		}
    // 	/>
    // </ScreenLayout>

    <ScreenLayout
      controlsList={!demo && !isJnj && controls}
      title={t("page.library.title")}
    >
      <TableLayout
        serverStatus={
          isJnj || servier_test_server || sanofi_test_server
            ? "success"
            : status
        }
        columns={columns}
        data={tableData}
        dataSort={sort}
        selectSort={handleSelectSort}
        dataSize={size}
        selectSize={handleSelectSize}
        searchPlaceholder={t("table.search_videos")}
        searchValue={search}
        onSearch={handleSearch}
        totalPages={tableTotalPages}
        currentPage={page}
        toPage={goToPage}
        setSelected={(items) => dispatch(setSelectedData(items))}
        filterCheckbox={
          !demo &&
          !isJnj && (
            <RootCheckbox
              onChange={() => handleCheckFilter()}
              label={t("page.library.checkbox_filter")}
            />
          )
        }
      />
    </ScreenLayout>
  );
};
